import React, { useEffect, useState, useRef } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useApp } from '../context/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Palette, Upload, Image, Type, Square, Gauge, Trash2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminSettingsPage = () => {
  const { getAuthHeaders } = useApp();
  
  // Get current language from localStorage or navigator
  const getCurrentLang = () => {
    const stored = localStorage.getItem('i18nextLng') || localStorage.getItem('language');
    if (stored) return stored.split('-')[0];
    return navigator.language?.split('-')[0] || 'ro';
  };
  const currentLang = getCurrentLang();
  
  const [settings, setSettings] = useState(null);
  const [colorSettings, setColorSettings] = useState({
    status_available: '#22c55e',
    status_occupied: '#ef4444',
    status_available_soon: '#f59e0b',
    label_promo: '#facc15',
    label_recommended: '#3b82f6',
    label_new: '#10b981'
  });
  const [loading, setLoading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingUtility, setUploadingUtility] = useState({ electric: false, gas: false, water: false });
  
  // Refs for file inputs
  const electricImageRef = useRef(null);
  const gasImageRef = useRef(null);
  const waterImageRef = useRef(null);
  
  // Translations for utility section
  const utilityTexts = {
    ro: {
      customize: 'Personalizează textul placeholder și imaginile demonstrative pentru fiecare tip de contor:',
      electricPlaceholder: '⚡ Index Electric - Placeholder',
      electricImage: 'Imagine Exemplu Electric',
      gasPlaceholder: '🔥 Index Gaz - Placeholder',
      gasImage: 'Imagine Exemplu Gaz',
      waterPlaceholder: '💧 Index Apă - Placeholder',
      waterImage: 'Imagine Exemplu Apă',
      uploadTip: '💡 Apăsați pe butonul Upload pentru a încărca imaginile direct pe server.',
      upload: 'Upload',
      delete: 'Șterge',
      active: 'Activ',
      disabled: 'Dezactivat'
    },
    en: {
      customize: 'Customize placeholder text and demo images for each meter type:',
      electricPlaceholder: '⚡ Electric Index - Placeholder',
      electricImage: 'Electric Example Image',
      gasPlaceholder: '🔥 Gas Index - Placeholder',
      gasImage: 'Gas Example Image',
      waterPlaceholder: '💧 Water Index - Placeholder',
      waterImage: 'Water Example Image',
      uploadTip: '💡 Click the Upload button to upload images directly to the server.',
      upload: 'Upload',
      delete: 'Delete',
      active: 'Active',
      disabled: 'Disabled'
    },
    fr: {
      customize: 'Personnalisez le texte du placeholder et les images de démonstration pour chaque type de compteur:',
      electricPlaceholder: '⚡ Index Électrique - Placeholder',
      electricImage: 'Image Exemple Électrique',
      gasPlaceholder: '🔥 Index Gaz - Placeholder',
      gasImage: 'Image Exemple Gaz',
      waterPlaceholder: '💧 Index Eau - Placeholder',
      waterImage: 'Image Exemple Eau',
      uploadTip: '💡 Cliquez sur le bouton Télécharger pour télécharger les images directement sur le serveur.',
      upload: 'Télécharger',
      delete: 'Supprimer',
      active: 'Actif',
      disabled: 'Désactivé'
    }
  };
  
  const ut = utilityTexts[currentLang] || utilityTexts.ro;

  useEffect(() => {
    fetchSettings();
    fetchColorSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/global`);
      // Set defaults for new theme fields if not present
      setSettings({
        ...data,
        site_text_color: data.site_text_color || '#ffffff',
        site_background_color: data.site_background_color || '#0f172a',
        site_background_transparency: data.site_background_transparency ?? 1.0,
        site_primary_color: data.site_primary_color || '#d4a853',
        site_header_transparency: data.site_header_transparency ?? 0.9,
        site_card_transparency: data.site_card_transparency ?? 0.8,
        enable_utility_index: data.enable_utility_index !== false
      });
    } catch (error) {
      toast.error('Failed to fetch settings');
    }
  };

  // Handle cover image upload
  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploadingCover(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/upload/image`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      // Use the relative URL returned by the server
      const imageUrl = data.url.startsWith('/api/') ? data.url : `/api/images/${data.url.split('/').pop()}`;
      setSettings({ ...settings, cover_image_url: imageUrl });
      toast.success('Cover image uploaded!');
    } catch (error) {
      toast.error('Failed to upload cover image');
    } finally {
      setUploadingCover(false);
    }
  };

  // Handle utility image upload
  const handleUtilityImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploadingUtility(prev => ({ ...prev, [type]: true }));
    try {
      const { data } = await axios.post(`${BACKEND_URL}/upload/image`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = data.url.startsWith('/api/') ? data.url : `/api/images/${data.url.split('/').pop()}`;
      const fieldName = `utility_${type}_image`;
      setSettings({ ...settings, [fieldName]: imageUrl });
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} image uploaded!`);
    } catch (error) {
      toast.error(`Failed to upload ${type} image`);
    } finally {
      setUploadingUtility(prev => ({ ...prev, [type]: false }));
    }
  };

  // Delete utility image
  const handleDeleteUtilityImage = (type) => {
    const fieldName = `utility_${type}_image`;
    setSettings({ ...settings, [fieldName]: '' });
    toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} image removed`);
  };

  // Helper to get full image URL for display
  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
    return url;
  };

  const fetchColorSettings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/colors`);
      setColorSettings(data);
    } catch (error) {
      console.log('Using default colors');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Save global settings
      await axios.put(`${BACKEND_URL}/api/settings/global`, settings, {
        headers: getAuthHeaders(),
      });
      // Save color settings
      await axios.put(`${BACKEND_URL}/api/settings/colors`, colorSettings, {
        headers: getAuthHeaders(),
      });
      toast.success('Settings updated!');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  if (!settings) return <AdminLayout><div>Loading...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div data-testid="admin-settings-page">
        <h1 className="text-4xl font-serif mb-8">Global Settings</h1>

        <form onSubmit={handleSubmit} className="glass p-8 space-y-8">
          {/* Site Theme Colors - NEW SECTION */}
          <div className="border-b border-white/10 pb-8">
            <div className="flex items-center gap-3 mb-6">
              <Palette size={24} className="text-primary" />
              <div>
                <h3 className="text-lg font-semibold">Culori Site & Transparență</h3>
                <p className="text-xs text-muted-foreground">Personalizează aspectul general al site-ului</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Text Color */}
              <div className="p-4 bg-slate-900/50 border border-white/10 rounded">
                <div className="flex items-center gap-3 mb-3">
                  <Type size={18} className="text-primary" />
                  <span className="text-sm font-medium">Culoare Text</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.site_text_color || '#ffffff'}
                    onChange={(e) => setSettings({ ...settings, site_text_color: e.target.value })}
                    className="w-12 h-10 rounded cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={settings.site_text_color || '#ffffff'}
                    onChange={(e) => setSettings({ ...settings, site_text_color: e.target.value })}
                    className="flex-1 bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary uppercase"
                  />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">Culoarea textului principal</p>
              </div>

              {/* Background Color */}
              <div className="p-4 bg-slate-900/50 border border-white/10 rounded">
                <div className="flex items-center gap-3 mb-3">
                  <Square size={18} className="text-primary" />
                  <span className="text-sm font-medium">Culoare Fundal</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.site_background_color || '#0f172a'}
                    onChange={(e) => setSettings({ ...settings, site_background_color: e.target.value })}
                    className="w-12 h-10 rounded cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={settings.site_background_color || '#0f172a'}
                    onChange={(e) => setSettings({ ...settings, site_background_color: e.target.value })}
                    className="flex-1 bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary uppercase"
                  />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">Culoarea de fundal a site-ului</p>
              </div>

              {/* Primary/Accent Color */}
              <div className="p-4 bg-slate-900/50 border border-white/10 rounded">
                <div className="flex items-center gap-3 mb-3">
                  <Palette size={18} className="text-primary" />
                  <span className="text-sm font-medium">Culoare Accent</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.site_primary_color || '#d4a853'}
                    onChange={(e) => setSettings({ ...settings, site_primary_color: e.target.value })}
                    className="w-12 h-10 rounded cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={settings.site_primary_color || '#d4a853'}
                    onChange={(e) => setSettings({ ...settings, site_primary_color: e.target.value })}
                    className="flex-1 bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary uppercase"
                  />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">Culoarea butoanelor și accentelor</p>
              </div>
            </div>

            {/* Transparency Settings */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="p-4 bg-slate-900/50 border border-white/10 rounded">
                <label className="text-sm font-medium block mb-3">Transparență Fundal</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settings.site_background_transparency ?? 1}
                    onChange={(e) => setSettings({ ...settings, site_background_transparency: parseFloat(e.target.value) })}
                    className="flex-1 accent-primary"
                  />
                  <span className="text-sm w-12 text-right">{Math.round((settings.site_background_transparency ?? 1) * 100)}%</span>
                </div>
              </div>

              <div className="p-4 bg-slate-900/50 border border-white/10 rounded">
                <label className="text-sm font-medium block mb-3">Transparență Header</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settings.site_header_transparency ?? 0.9}
                    onChange={(e) => setSettings({ ...settings, site_header_transparency: parseFloat(e.target.value) })}
                    className="flex-1 accent-primary"
                  />
                  <span className="text-sm w-12 text-right">{Math.round((settings.site_header_transparency ?? 0.9) * 100)}%</span>
                </div>
              </div>

              <div className="p-4 bg-slate-900/50 border border-white/10 rounded">
                <label className="text-sm font-medium block mb-3">Transparență Carduri</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settings.site_card_transparency ?? 0.8}
                    onChange={(e) => setSettings({ ...settings, site_card_transparency: parseFloat(e.target.value) })}
                    className="flex-1 accent-primary"
                  />
                  <span className="text-sm w-12 text-right">{Math.round((settings.site_card_transparency ?? 0.8) * 100)}%</span>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="mt-6 p-4 rounded-lg" style={{ 
              backgroundColor: settings.site_background_color || '#0f172a',
              opacity: settings.site_background_transparency ?? 1
            }}>
              <p className="text-sm mb-2" style={{ color: settings.site_text_color || '#ffffff' }}>
                Preview text cu culorile selectate
              </p>
              <button 
                type="button"
                className="px-4 py-2 rounded text-sm font-medium"
                style={{ backgroundColor: settings.site_primary_color || '#d4a853', color: '#000' }}
              >
                Buton Accent
              </button>
            </div>
          </div>

          {/* Utility Index Toggle */}
          <div className="glass p-6 border border-cyan-500/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-cyan-500/20 rounded-lg">
                  <Gauge className="text-cyan-400" size={24} />
                </div>
                <div>
                  <h3 className="font-medium">Transmitere Index (Utilități)</h3>
                  <p className="text-sm text-muted-foreground">
                    Permite vizitatorilor să trimită indexurile contoarelor (electric, gaz, apă)
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.enable_utility_index !== false}
                  onChange={(e) => setSettings({ ...settings, enable_utility_index: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-cyan-500"></div>
                <span className="ms-3 text-sm font-medium">
                  {settings.enable_utility_index !== false ? ut.active : ut.disabled}
                </span>
              </label>
            </div>
            
            {settings.enable_utility_index !== false && (
              <div className="mt-6 space-y-6 pt-6 border-t border-white/10">
                <p className="text-sm text-muted-foreground">
                  {ut.customize}
                </p>
                
                {/* Hidden file inputs */}
                <input type="file" ref={electricImageRef} className="hidden" accept="image/*" onChange={(e) => handleUtilityImageUpload(e, 'electric')} />
                <input type="file" ref={gasImageRef} className="hidden" accept="image/*" onChange={(e) => handleUtilityImageUpload(e, 'gas')} />
                <input type="file" ref={waterImageRef} className="hidden" accept="image/*" onChange={(e) => handleUtilityImageUpload(e, 'water')} />
                
                {/* Electric */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-yellow-400 block mb-2">
                      {ut.electricPlaceholder}
                    </label>
                    <input
                      type="text"
                      value={settings.utility_electric_placeholder || ''}
                      onChange={(e) => setSettings({ ...settings, utility_electric_placeholder: e.target.value })}
                      placeholder="ex: 12345.67"
                      className="w-full px-3 py-2 bg-slate-900 border border-white/10 rounded focus:border-yellow-400 focus:outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-yellow-400 block mb-2">
                      {ut.electricImage}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={settings.utility_electric_image || ''}
                        onChange={(e) => setSettings({ ...settings, utility_electric_image: e.target.value })}
                        placeholder="URL"
                        className="flex-1 px-3 py-2 bg-slate-900 border border-white/10 rounded focus:border-yellow-400 focus:outline-none text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => electricImageRef.current?.click()}
                        disabled={uploadingUtility.electric}
                        className="px-3 py-2 bg-yellow-500/20 text-yellow-400 rounded hover:bg-yellow-500/30 text-sm flex items-center gap-1"
                      >
                        <Upload size={14} />
                        {uploadingUtility.electric ? '...' : ut.upload}
                      </button>
                      {settings.utility_electric_image && (
                        <button
                          type="button"
                          onClick={() => handleDeleteUtilityImage('electric')}
                          className="px-2 py-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    {settings.utility_electric_image && (
                      <img src={getFullImageUrl(settings.utility_electric_image)} alt="Electric" className="mt-2 h-20 object-contain rounded" />
                    )}
                  </div>
                </div>

                {/* Gas */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-orange-500/5 border border-orange-500/20 rounded-lg">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-orange-400 block mb-2">
                      {ut.gasPlaceholder}
                    </label>
                    <input
                      type="text"
                      value={settings.utility_gas_placeholder || ''}
                      onChange={(e) => setSettings({ ...settings, utility_gas_placeholder: e.target.value })}
                      placeholder="ex: 1234.56"
                      className="w-full px-3 py-2 bg-slate-900 border border-white/10 rounded focus:border-orange-400 focus:outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-orange-400 block mb-2">
                      {ut.gasImage}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={settings.utility_gas_image || ''}
                        onChange={(e) => setSettings({ ...settings, utility_gas_image: e.target.value })}
                        placeholder="URL"
                        className="flex-1 px-3 py-2 bg-slate-900 border border-white/10 rounded focus:border-orange-400 focus:outline-none text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => gasImageRef.current?.click()}
                        disabled={uploadingUtility.gas}
                        className="px-3 py-2 bg-orange-500/20 text-orange-400 rounded hover:bg-orange-500/30 text-sm flex items-center gap-1"
                      >
                        <Upload size={14} />
                        {uploadingUtility.gas ? '...' : ut.upload}
                      </button>
                      {settings.utility_gas_image && (
                        <button
                          type="button"
                          onClick={() => handleDeleteUtilityImage('gas')}
                          className="px-2 py-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    {settings.utility_gas_image && (
                      <img src={getFullImageUrl(settings.utility_gas_image)} alt="Gas" className="mt-2 h-20 object-contain rounded" />
                    )}
                  </div>
                </div>

                {/* Water */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-blue-400 block mb-2">
                      {ut.waterPlaceholder}
                    </label>
                    <input
                      type="text"
                      value={settings.utility_water_placeholder || ''}
                      onChange={(e) => setSettings({ ...settings, utility_water_placeholder: e.target.value })}
                      placeholder="ex: 123.45"
                      className="w-full px-3 py-2 bg-slate-900 border border-white/10 rounded focus:border-blue-400 focus:outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-blue-400 block mb-2">
                      {ut.waterImage}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={settings.utility_water_image || ''}
                        onChange={(e) => setSettings({ ...settings, utility_water_image: e.target.value })}
                        placeholder="URL"
                        className="flex-1 px-3 py-2 bg-slate-900 border border-white/10 rounded focus:border-blue-400 focus:outline-none text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => waterImageRef.current?.click()}
                        disabled={uploadingUtility.water}
                        className="px-3 py-2 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 text-sm flex items-center gap-1"
                      >
                        <Upload size={14} />
                        {uploadingUtility.water ? '...' : ut.upload}
                      </button>
                      {settings.utility_water_image && (
                        <button
                          type="button"
                          onClick={() => handleDeleteUtilityImage('water')}
                          className="px-2 py-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    {settings.utility_water_image && (
                      <img src={getFullImageUrl(settings.utility_water_image)} alt="Water" className="mt-2 h-20 object-contain rounded" />
                    )}
                  </div>
                </div>
                
                <p className="text-xs text-cyan-400/70">
                  {ut.uploadTip}
                </p>
              </div>
            )}
          </div>

          {/* Cover Image with Upload */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Image size={20} className="text-primary" />
              <label className="text-xs uppercase tracking-wider text-primary">Cover Image (Hero)</label>
            </div>
            
            <div className="flex flex-col gap-4">
              {/* Upload Button */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 px-4 py-2 bg-primary/20 hover:bg-primary/30 border border-primary/50 rounded cursor-pointer transition-colors">
                  <Upload size={18} />
                  <span className="text-sm">{uploadingCover ? 'Se încarcă...' : 'Încarcă imagine'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverUpload}
                    disabled={uploadingCover}
                    className="hidden"
                  />
                </label>
                <span className="text-xs text-muted-foreground">sau introdu URL manual:</span>
              </div>

              {/* URL Input */}
              <input
                type="text"
                value={settings.cover_image_url}
                onChange={(e) => setSettings({ ...settings, cover_image_url: e.target.value })}
                placeholder="https://example.com/image.jpg sau /api/images/..."
                className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary"
              />

              {/* Preview */}
              {settings.cover_image_url && (
                <div className="relative">
                  <img 
                    src={getFullImageUrl(settings.cover_image_url)} 
                    alt="Cover Preview" 
                    className="w-full h-48 object-cover rounded-lg"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <button
                    type="button"
                    onClick={() => setSettings({ ...settings, cover_image_url: '' })}
                    className="absolute top-2 right-2 p-1 bg-red-500/80 hover:bg-red-500 rounded text-white text-xs"
                  >
                    ✕ Șterge
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* WhatsApp */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-xs uppercase tracking-wider text-primary mb-2 block">WhatsApp Number</label>
              <input
                type="tel"
                placeholder="+40123456789"
                value={settings.whatsapp_number}
                onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary"
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="enable-whatsapp"
                checked={settings.enable_whatsapp}
                onChange={(e) => setSettings({ ...settings, enable_whatsapp: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="enable-whatsapp" className="text-sm">Enable WhatsApp Button</label>
            </div>
          </div>

          {/* WhatsApp Message */}
          <div>
            <label className="text-xs uppercase tracking-wider text-primary mb-2 block">WhatsApp Default Message</label>
            <div className="grid grid-cols-3 gap-4">
              <textarea
                placeholder="Romanian"
                value={settings.whatsapp_message?.ro || ''}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    whatsapp_message: { ...settings.whatsapp_message, ro: e.target.value },
                  })
                }
                rows={2}
                className="bg-transparent border border-white/20 p-3 text-sm focus:outline-none focus:border-primary resize-none"
              />
              <textarea
                placeholder="English"
                value={settings.whatsapp_message?.en || ''}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    whatsapp_message: { ...settings.whatsapp_message, en: e.target.value },
                  })
                }
                rows={2}
                className="bg-transparent border border-white/20 p-3 text-sm focus:outline-none focus:border-primary resize-none"
              />
              <textarea
                placeholder="French"
                value={settings.whatsapp_message?.fr || ''}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    whatsapp_message: { ...settings.whatsapp_message, fr: e.target.value },
                  })
                }
                rows={2}
                className="bg-transparent border border-white/20 p-3 text-sm focus:outline-none focus:border-primary resize-none"
              />
            </div>
          </div>

          {/* Currency */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Exchange Rate (EUR to RON)</label>
              <input
                type="number"
                step="0.01"
                value={settings.exchange_rate}
                onChange={(e) => setSettings({ ...settings, exchange_rate: parseFloat(e.target.value) })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Default Currency</label>
              <select
                value={settings.default_currency}
                onChange={(e) => setSettings({ ...settings, default_currency: e.target.value })}
                className="w-full bg-slate-900 border border-white/20 py-3 px-3 text-sm"
              >
                <option value="EUR">EUR</option>
                <option value="RON">RON</option>
              </select>
            </div>
          </div>

          {/* Cookie Banner */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                id="enable-cookie"
                checked={settings.enable_cookie_banner}
                onChange={(e) => setSettings({ ...settings, enable_cookie_banner: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="enable-cookie" className="text-sm">Enable Cookie Banner</label>
            </div>
            <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Cookie Banner Text</label>
            <div className="grid grid-cols-3 gap-4">
              <textarea
                placeholder="Romanian"
                value={settings.cookie_banner_text?.ro || ''}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    cookie_banner_text: { ...settings.cookie_banner_text, ro: e.target.value },
                  })
                }
                rows={3}
                className="bg-transparent border border-white/20 p-3 text-sm focus:outline-none focus:border-primary resize-none"
              />
              <textarea
                placeholder="English"
                value={settings.cookie_banner_text?.en || ''}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    cookie_banner_text: { ...settings.cookie_banner_text, en: e.target.value },
                  })
                }
                rows={3}
                className="bg-transparent border border-white/20 p-3 text-sm focus:outline-none focus:border-primary resize-none"
              />
              <textarea
                placeholder="French"
                value={settings.cookie_banner_text?.fr || ''}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    cookie_banner_text: { ...settings.cookie_banner_text, fr: e.target.value },
                  })
                }
                rows={3}
                className="bg-transparent border border-white/20 p-3 text-sm focus:outline-none focus:border-primary resize-none"
              />
            </div>
          </div>

          {/* Footer Text */}
          <div>
            <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Footer Copyright Text</label>
            <div className="grid grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Romanian"
                value={settings.footer_text?.ro || ''}
                onChange={(e) =>
                  setSettings({ ...settings, footer_text: { ...settings.footer_text, ro: e.target.value } })
                }
                className="bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary"
              />
              <input
                type="text"
                placeholder="English"
                value={settings.footer_text?.en || ''}
                onChange={(e) =>
                  setSettings({ ...settings, footer_text: { ...settings.footer_text, en: e.target.value } })
                }
                className="bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary"
              />
              <input
                type="text"
                placeholder="French"
                value={settings.footer_text?.fr || ''}
                onChange={(e) =>
                  setSettings({ ...settings, footer_text: { ...settings.footer_text, fr: e.target.value } })
                }
                className="bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Footer Tagline/Description */}
          <div>
            <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Footer Tagline (under logo)</label>
            <p className="text-xs text-muted-foreground mb-3">This text appears below RENTIASI.RO in the footer</p>
            <div className="grid grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Romanian"
                value={settings.footer_tagline?.ro || ''}
                onChange={(e) =>
                  setSettings({ ...settings, footer_tagline: { ...settings.footer_tagline, ro: e.target.value } })
                }
                className="bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary"
              />
              <input
                type="text"
                placeholder="English"
                value={settings.footer_tagline?.en || ''}
                onChange={(e) =>
                  setSettings({ ...settings, footer_tagline: { ...settings.footer_tagline, en: e.target.value } })
                }
                className="bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary"
              />
              <input
                type="text"
                placeholder="French"
                value={settings.footer_tagline?.fr || ''}
                onChange={(e) =>
                  setSettings({ ...settings, footer_tagline: { ...settings.footer_tagline, fr: e.target.value } })
                }
                className="bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Home Page Features */}
          <div>
            <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Home Page Features</label>
            <p className="text-xs text-muted-foreground mb-4">Edit the 3 feature boxes shown on the home page</p>
            
            {(settings.home_features || []).map((feature, index) => (
              <div key={index} className="mb-6 p-4 bg-slate-900/50 border border-white/10 rounded">
                <h4 className="text-sm font-semibold text-primary mb-3">Feature {index + 1}</h4>
                
                {/* Title */}
                <div className="mb-4">
                  <label className="text-xs text-muted-foreground mb-2 block">Title</label>
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="text"
                      placeholder="Romanian"
                      value={feature.title?.ro || ''}
                      onChange={(e) => {
                        const newFeatures = [...settings.home_features];
                        newFeatures[index] = {
                          ...newFeatures[index],
                          title: { ...newFeatures[index].title, ro: e.target.value }
                        };
                        setSettings({ ...settings, home_features: newFeatures });
                      }}
                      className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                    <input
                      type="text"
                      placeholder="English"
                      value={feature.title?.en || ''}
                      onChange={(e) => {
                        const newFeatures = [...settings.home_features];
                        newFeatures[index] = {
                          ...newFeatures[index],
                          title: { ...newFeatures[index].title, en: e.target.value }
                        };
                        setSettings({ ...settings, home_features: newFeatures });
                      }}
                      className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                    <input
                      type="text"
                      placeholder="French"
                      value={feature.title?.fr || ''}
                      onChange={(e) => {
                        const newFeatures = [...settings.home_features];
                        newFeatures[index] = {
                          ...newFeatures[index],
                          title: { ...newFeatures[index].title, fr: e.target.value }
                        };
                        setSettings({ ...settings, home_features: newFeatures });
                      }}
                      className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">Description</label>
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="text"
                      placeholder="Romanian"
                      value={feature.description?.ro || ''}
                      onChange={(e) => {
                        const newFeatures = [...settings.home_features];
                        newFeatures[index] = {
                          ...newFeatures[index],
                          description: { ...newFeatures[index].description, ro: e.target.value }
                        };
                        setSettings({ ...settings, home_features: newFeatures });
                      }}
                      className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                    <input
                      type="text"
                      placeholder="English"
                      value={feature.description?.en || ''}
                      onChange={(e) => {
                        const newFeatures = [...settings.home_features];
                        newFeatures[index] = {
                          ...newFeatures[index],
                          description: { ...newFeatures[index].description, en: e.target.value }
                        };
                        setSettings({ ...settings, home_features: newFeatures });
                      }}
                      className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                    <input
                      type="text"
                      placeholder="French"
                      value={feature.description?.fr || ''}
                      onChange={(e) => {
                        const newFeatures = [...settings.home_features];
                        newFeatures[index] = {
                          ...newFeatures[index],
                          description: { ...newFeatures[index].description, fr: e.target.value }
                        };
                        setSettings({ ...settings, home_features: newFeatures });
                      }}
                      className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer Links */}
          <div>
            <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Footer Links (Legal Pages)</label>
            <p className="text-xs text-muted-foreground mb-4">Edit the 6 legal/policy links shown at the bottom of the footer</p>
            
            {(settings.footer_links || []).map((link, index) => (
              <div key={index} className="mb-4 p-4 bg-slate-900/50 border border-white/10 rounded">
                <h4 className="text-sm font-semibold text-primary mb-3">Link {index + 1}</h4>
                
                {/* Link Name */}
                <div className="mb-3">
                  <label className="text-xs text-muted-foreground mb-2 block">Name (multilingual)</label>
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="text"
                      placeholder="Romanian"
                      value={link.name?.ro || ''}
                      onChange={(e) => {
                        const newLinks = [...settings.footer_links];
                        newLinks[index] = {
                          ...newLinks[index],
                          name: { ...newLinks[index].name, ro: e.target.value }
                        };
                        setSettings({ ...settings, footer_links: newLinks });
                      }}
                      className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                    <input
                      type="text"
                      placeholder="English"
                      value={link.name?.en || ''}
                      onChange={(e) => {
                        const newLinks = [...settings.footer_links];
                        newLinks[index] = {
                          ...newLinks[index],
                          name: { ...newLinks[index].name, en: e.target.value }
                        };
                        setSettings({ ...settings, footer_links: newLinks });
                      }}
                      className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                    <input
                      type="text"
                      placeholder="French"
                      value={link.name?.fr || ''}
                      onChange={(e) => {
                        const newLinks = [...settings.footer_links];
                        newLinks[index] = {
                          ...newLinks[index],
                          name: { ...newLinks[index].name, fr: e.target.value }
                        };
                        setSettings({ ...settings, footer_links: newLinks });
                      }}
                      className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>

                {/* Link URL */}
                <div className="mb-3">
                  <label className="text-xs text-muted-foreground mb-2 block">URL (internal like /privacy or external like https://...)</label>
                  <input
                    type="text"
                    placeholder="/privacy or https://example.com"
                    value={link.url || ''}
                    onChange={(e) => {
                      const newLinks = [...settings.footer_links];
                      newLinks[index] = {
                        ...newLinks[index],
                        url: e.target.value
                      };
                      setSettings({ ...settings, footer_links: newLinks });
                    }}
                    className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
                  />
                </div>

                {/* Logo URL */}
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">Logo URL (optional - for SAL/ANPC, ODR badges)</label>
                  <input
                    type="text"
                    placeholder="https://example.com/logo.png"
                    value={link.logo_url || ''}
                    onChange={(e) => {
                      const newLinks = [...settings.footer_links];
                      newLinks[index] = {
                        ...newLinks[index],
                        logo_url: e.target.value
                      };
                      setSettings({ ...settings, footer_links: newLinks });
                    }}
                    className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
                  />
                  {link.logo_url && (
                    <img src={link.logo_url} alt="Logo preview" className="mt-2 h-10 object-contain bg-white/10 p-1 rounded" />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Status & Label Colors */}
          <div className="border-t border-white/10 pt-8">
            <div className="flex items-center gap-3 mb-6">
              <Palette size={24} className="text-primary" />
              <div>
                <h3 className="text-lg font-semibold">Culori Status & Etichete</h3>
                <p className="text-xs text-muted-foreground">Personalizează culorile pentru statusuri și etichete apartamente</p>
              </div>
            </div>
            
            {/* Status Colors */}
            <div className="mb-6">
              <label className="text-xs uppercase tracking-wider text-primary mb-4 block">Culori Status</label>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-slate-900/50 border border-white/10 rounded">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-green-400">✓</span>
                    <span className="text-sm font-medium">Available</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={colorSettings.status_available}
                      onChange={(e) => setColorSettings({ ...colorSettings, status_available: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={colorSettings.status_available}
                      onChange={(e) => setColorSettings({ ...colorSettings, status_available: e.target.value })}
                      className="flex-1 bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary uppercase"
                    />
                  </div>
                  <div 
                    className="mt-3 px-3 py-1 text-white text-xs font-bold uppercase tracking-wider rounded text-center"
                    style={{ backgroundColor: colorSettings.status_available }}
                  >
                    Available
                  </div>
                </div>

                <div className="p-4 bg-slate-900/50 border border-white/10 rounded">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-red-400">✗</span>
                    <span className="text-sm font-medium">Occupied</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={colorSettings.status_occupied}
                      onChange={(e) => setColorSettings({ ...colorSettings, status_occupied: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={colorSettings.status_occupied}
                      onChange={(e) => setColorSettings({ ...colorSettings, status_occupied: e.target.value })}
                      className="flex-1 bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary uppercase"
                    />
                  </div>
                  <div 
                    className="mt-3 px-3 py-1 text-white text-xs font-bold uppercase tracking-wider rounded text-center"
                    style={{ backgroundColor: colorSettings.status_occupied }}
                  >
                    Occupied
                  </div>
                </div>

                <div className="p-4 bg-slate-900/50 border border-white/10 rounded">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-amber-400">🕐</span>
                    <span className="text-sm font-medium">Available Soon</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={colorSettings.status_available_soon}
                      onChange={(e) => setColorSettings({ ...colorSettings, status_available_soon: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={colorSettings.status_available_soon}
                      onChange={(e) => setColorSettings({ ...colorSettings, status_available_soon: e.target.value })}
                      className="flex-1 bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary uppercase"
                    />
                  </div>
                  <div 
                    className="mt-3 px-3 py-1 text-white text-xs font-bold uppercase tracking-wider rounded text-center"
                    style={{ backgroundColor: colorSettings.status_available_soon }}
                  >
                    Available Soon
                  </div>
                </div>
              </div>
            </div>

            {/* Label Colors */}
            <div>
              <label className="text-xs uppercase tracking-wider text-primary mb-4 block">Culori Etichete</label>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-slate-900/50 border border-white/10 rounded">
                  <div className="flex items-center gap-3 mb-3">
                    <span>🏷️</span>
                    <span className="text-sm font-medium">PROMO</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={colorSettings.label_promo}
                      onChange={(e) => setColorSettings({ ...colorSettings, label_promo: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={colorSettings.label_promo}
                      onChange={(e) => setColorSettings({ ...colorSettings, label_promo: e.target.value })}
                      className="flex-1 bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary uppercase"
                    />
                  </div>
                  <div 
                    className="mt-3 px-3 py-1 text-black text-xs font-bold uppercase tracking-wider rounded text-center"
                    style={{ backgroundColor: colorSettings.label_promo }}
                  >
                    PROMO
                  </div>
                </div>

                <div className="p-4 bg-slate-900/50 border border-white/10 rounded">
                  <div className="flex items-center gap-3 mb-3">
                    <span>⭐</span>
                    <span className="text-sm font-medium">RECOMMENDED</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={colorSettings.label_recommended}
                      onChange={(e) => setColorSettings({ ...colorSettings, label_recommended: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={colorSettings.label_recommended}
                      onChange={(e) => setColorSettings({ ...colorSettings, label_recommended: e.target.value })}
                      className="flex-1 bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary uppercase"
                    />
                  </div>
                  <div 
                    className="mt-3 px-3 py-1 text-white text-xs font-bold uppercase tracking-wider rounded text-center"
                    style={{ backgroundColor: colorSettings.label_recommended }}
                  >
                    RECOMMENDED
                  </div>
                </div>

                <div className="p-4 bg-slate-900/50 border border-white/10 rounded">
                  <div className="flex items-center gap-3 mb-3">
                    <span>🆕</span>
                    <span className="text-sm font-medium">NEW</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={colorSettings.label_new}
                      onChange={(e) => setColorSettings({ ...colorSettings, label_new: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={colorSettings.label_new}
                      onChange={(e) => setColorSettings({ ...colorSettings, label_new: e.target.value })}
                      className="flex-1 bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary uppercase"
                    />
                  </div>
                  <div 
                    className="mt-3 px-3 py-1 text-white text-xs font-bold uppercase tracking-wider rounded text-center"
                    style={{ backgroundColor: colorSettings.label_new }}
                  >
                    NEW
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Button type="submit" disabled={loading} data-testid="save-settings-button" className="w-full bg-primary text-primary-foreground">
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminSettingsPage;